import { CalloutBlock, StyledP, StyledLabel } from 'components';
import { Show } from 'components/Functional';
import { isNotEmpty } from 'helpers/arrays';
import { showIfOrElse, showIf, any } from 'helpers/conditionals';
import React, { useRef, useEffect } from 'react';
import hash from 'object-hash';
import { connect, useDispatch } from 'react-redux';
import {
  getAppliedStoreCreditFromTotalSegments,
  getPromoCodeDiscountFromTotalSegments,
  getGrandTotalFromTotalSegments,
  getShippingTotalFromTotalSegments,
  getTaxFromTotalSegments,
  getGiftCardsFromTotalSegments,
  getPromoCodeDiscountValueFromTotalSegments,
  getSubtotalFromCartItems
} from 'store/cart/magentoSelectors';
import {
  cartLoadingStateSelector,
  isVirtualCartSelector,
  shippingRatesLoadingStateSelector,
  shippingAddressLoadingStateSelector,
  cartItemsLoadingStateSelector,
  totalSegmentsSelector,
  shippingRatesSelector,
  cartItemsSelector,
  shippingAddressSelector,
  shippingMethodSelector,
  cartPaymentMethodsSelector
} from 'store/cart/selectors';

import { GlobalState, LoadingState, isFetching } from 'store/constants';

import { CartSummaryItem, CartSummaryWrapper, CartSummaryGroup } from './styled';
import type { Dispatch } from 'redux';
import {
  removePromoCodeRequest,
  removeGiftCardRequest,
  removeStoreCreditRequest,
  fetchShippingRatesRequest as _fetchShippingRatesRequest
} from 'store/cart/actions';
import { applyAfterpayChecksum } from 'features/afterpay/store/actions';
import QuoteDataTotalSegment from 'mage-swagfaces/quote/QuoteDataTotalSegment';
import { isDiscountAPromoCode } from 'helpers/mage-helpers/magento';
import { SmallLoader } from 'components/AULoader/SmallLoader';
import { AfterpayMessaging } from 'features/afterpay/components/AfterpayMessaging';
import useAfterpay from 'features/afterpay/hooks/useAfterpay';
import { clearMessagingPlacements } from 'features/afterpay/helpers';
import { FREE_SHIPPING_METHOD_NAME } from 'store/ui/constants';
import { getShippingCost, isFreeShippingSelected } from 'helpers/shipping/freeshipping';
import QuoteDataCartItem from 'mage-swagfaces/quote/QuoteDataCartItem';
import { CustomerDataAddress } from 'au-js-sdk/lib/models/magento/customer/CustomerDataAddress';
import { buildShippingMethodStr } from 'components/Shipping/helpers';
import CartPrice from 'components/CartPrice';
import { objIsEmpty } from 'helpers/objects';
import { CartPaymentMethod, hasAfterpayPayment as _hasAfterpayPayment } from 'helpers/billing';
import useTrackComponentLoadTime from '../../analytics/itly/hooks/useTrackComponentLoadTime';
import { TrackingProp } from 'react-tracking';
import { ComponentName } from 'constants/componentName';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import { SPLIT_IO_FEATURE_FLAG_SHIPPING_TEST, SPLIT_IO_FEATURE_FLAG_SHIPPING_TEST_COOKIE } from 'constants/split';
import { getFilteredShippingRates } from 'helpers/shipping';
import { QuoteDataShippingMethod__Patched } from 'pages/checkout/cart';

export interface CartSummaryProps {
  cartLoadingState: LoadingState;
  shippingRatesLoadingState: LoadingState;
  shippingAddressLoadingState: LoadingState;
  cartItemsLoadingState: LoadingState;
  isVirtualCart: boolean;
  isFirstStep: boolean;
  totalSegments: QuoteDataTotalSegment[];
  shippingRates: QuoteDataShippingMethod__Patched[];
  shippingMethod: string;
  cartItems: QuoteDataCartItem[];
  shippingAddress: CustomerDataAddress;
  removePromoCode: () => void;
  removeGiftCard: (code: string) => void;
  removeStoreCredit: () => void;
  isFinalStep: boolean;
  cartPaymentMethods: CartPaymentMethod[];
  fetchShippingRatesRequest: () => void;
  tracking: TrackingProp;
  cartId: number;
  email?: string;
}

export const CartSummary: React.FC<CartSummaryProps> = ({
  cartLoadingState,
  shippingAddressLoadingState,
  cartItemsLoadingState,
  cartItems,
  shippingRates,
  shippingMethod,
  removePromoCode,
  removeGiftCard,
  removeStoreCredit,
  isVirtualCart,
  shippingRatesLoadingState,
  totalSegments,
  isFirstStep,
  shippingAddress,
  isFinalStep,
  cartPaymentMethods,
  fetchShippingRatesRequest,
  tracking,
  cartId,
  email
}) => {
  const dispatch = useDispatch();

  const cartFetching = any(isFetching(cartLoadingState), isFetching(cartItemsLoadingState));

  const ratesFetching = any(isFetching(shippingAddressLoadingState), isFetching(shippingRatesLoadingState));

  useTrackComponentLoadTime({
    isLoading: cartFetching || ratesFetching,
    tracking,
    cartId,
    componentName: ComponentName.SUMMARY,
    email
  });

  const { treatmentStatus: displayShippingTest } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_SHIPPING_TEST,
    SPLIT_IO_FEATURE_FLAG_SHIPPING_TEST_COOKIE
  );

  const hasShippingTotalFromCart =
    getShippingTotalFromTotalSegments(totalSegments) > 0 || isFreeShippingSelected(totalSegments);
  const filteredShippingRates = getFilteredShippingRates(shippingRates, displayShippingTest);
  const firstRate: QuoteDataShippingMethod__Patched = filteredShippingRates.filter(
    (rate) => rate.method_title !== FREE_SHIPPING_METHOD_NAME
  )[0];
  const cartGrandTotal = getGrandTotalFromTotalSegments(totalSegments);

  const loadingPlaceholder = (
    <StyledP secondary>
      <SmallLoader isLoading />
    </StyledP>
  );
  const { updateWidget, resetAfterpayFlow } = useAfterpay();

  useEffect(() => {
    if (!window.AfterPay || !window.afterpayWidget || cartFetching || ratesFetching) {
      clearMessagingPlacements();
      return;
    }
  }, [ratesFetching, cartFetching]);

  const hasCustomerShippingAddress = !objIsEmpty(shippingAddress);
  const hasAfterpayPayment = _hasAfterpayPayment(cartPaymentMethods);
  const isShippingShown = !isFirstStep || hasCustomerShippingAddress || hasAfterpayPayment;

  const requestedShipping = useRef(false);
  useEffect(() => {
    if (isShippingShown && cartItems.length > 0 && !requestedShipping.current) {
      fetchShippingRatesRequest();
      requestedShipping.current = true;
    }
  }, [cartItems.length, fetchShippingRatesRequest, isShippingShown]);

  useEffect(() => {
    if (!window.AfterPay || !window.afterpayWidget || !cartGrandTotal) {
      return;
    }
    const newCartChecksum = hash(cartGrandTotal.toString());

    dispatch(applyAfterpayChecksum(newCartChecksum));
    updateWidget(cartGrandTotal.toString());
    resetAfterpayFlow();
  }, [cartGrandTotal, dispatch, resetAfterpayFlow, updateWidget]);

  const shippingEstimate = () => {
    if (!isShippingShown) {
      return <p>Calculated on next step</p>;
    }

    const selectedRate = filteredShippingRates.find(
      (rate) => buildShippingMethodStr(rate.carrier_code)(rate.method_code) === shippingMethod
    );

    if (selectedRate && selectedRate.amount === 0) {
      return <p>Free</p>;
    }

    const shippingCost = selectedRate
      ? selectedRate.amount
      : getShippingCost(hasShippingTotalFromCart, totalSegments, firstRate, isVirtualCart);

    return <CartPrice value={shippingCost} />;
  };

  return (
    <CalloutBlock title="Summary">
      <CartSummaryWrapper>
        {(!isFirstStep || hasShippingTotalFromCart) && (
          <CartSummaryItem>
            <StyledP secondary>Subtotal</StyledP>
            {showIfOrElse(cartFetching)(loadingPlaceholder)(<CartPrice value={getSubtotalFromCartItems(cartItems)} />)}
          </CartSummaryItem>
        )}

        {isShippingShown ? (
          <Show when={!isVirtualCart}>
            <CartSummaryItem>
              <>
                {showIfOrElse(hasShippingTotalFromCart || isVirtualCart)(<StyledP secondary>Shipping</StyledP>)(
                  <StyledP secondary>Estimated Shipping</StyledP>
                )}
                {showIfOrElse(cartFetching || ratesFetching)(loadingPlaceholder)(shippingEstimate())}
              </>
            </CartSummaryItem>
          </Show>
        ) : (
          <CartSummaryItem>
            <StyledP secondary>Tax & Shipping</StyledP>
            {showIfOrElse(cartFetching || ratesFetching)(loadingPlaceholder)(
              <StyledP secondary>Calculated on next step</StyledP>
            )}
          </CartSummaryItem>
        )}

        {(!isFirstStep || hasShippingTotalFromCart) && (
          <CartSummaryItem>
            <StyledP secondary>Tax</StyledP>
            {showIfOrElse(cartFetching)(loadingPlaceholder)(
              <>
                <Show when={!!getTaxFromTotalSegments(totalSegments)}>
                  <CartPrice value={getTaxFromTotalSegments(totalSegments)} />
                </Show>
                <Show when={!getTaxFromTotalSegments(totalSegments) && !isVirtualCart}>
                  <StyledP secondary>―</StyledP>
                </Show>
              </>
            )}
          </CartSummaryItem>
        )}

        <Show when={!!getPromoCodeDiscountFromTotalSegments(totalSegments).value}>
          <CartSummaryGroup>
            <StyledP secondary>Promo Code</StyledP>
            {showIfOrElse(cartFetching)(loadingPlaceholder)(
              <CartSummaryItem>
                <CartSummaryItem>
                  <StyledLabel>{getPromoCodeDiscountFromTotalSegments(totalSegments).title}</StyledLabel>
                  <Show when={!!isDiscountAPromoCode(getPromoCodeDiscountFromTotalSegments(totalSegments))}>
                    <button onClick={() => removePromoCode()}>Remove</button>
                  </Show>
                </CartSummaryItem>
                <CartPrice value={getPromoCodeDiscountValueFromTotalSegments(totalSegments)} />
              </CartSummaryItem>
            )}
          </CartSummaryGroup>
        </Show>

        <Show when={isNotEmpty(getGiftCardsFromTotalSegments(totalSegments))}>
          <CartSummaryGroup>
            <StyledP secondary>Gift Card</StyledP>
            {cartFetching
              ? loadingPlaceholder
              : getGiftCardsFromTotalSegments(totalSegments).map((x, idx) => (
                  <CartSummaryItem key={idx}>
                    <CartSummaryItem>
                      <StyledLabel>{x.c}</StyledLabel>
                      <button onClick={() => removeGiftCard(x.c)}>Remove</button>
                    </CartSummaryItem>
                    <CartPrice value={-x.a} />
                  </CartSummaryItem>
                ))}
          </CartSummaryGroup>
        </Show>

        <Show when={!!getAppliedStoreCreditFromTotalSegments(totalSegments)}>
          <CartSummaryItem>
            <StyledP secondary>
              AU Credit
              <button onClick={() => removeStoreCredit()}>Remove</button>
            </StyledP>
            {showIfOrElse(cartFetching)(loadingPlaceholder)(
              <CartPrice value={getAppliedStoreCreditFromTotalSegments(totalSegments)} />
            )}
          </CartSummaryItem>
        </Show>

        {isFirstStep && !hasCustomerShippingAddress ? (
          <CartSummaryItem id="afterpay-messaging-widget">
            <StyledP secondary>Subtotal</StyledP>
            {showIfOrElse(cartFetching)(loadingPlaceholder)(
              <CartPrice
                className="afterpay-price-selector-mini-cart"
                value={getGrandTotalFromTotalSegments(totalSegments)}
              />
            )}
          </CartSummaryItem>
        ) : (
          <CartSummaryItem bold id="afterpay-messaging-widget">
            <StyledP secondary>
              {showIf(!getShippingTotalFromTotalSegments(totalSegments) || !getTaxFromTotalSegments(totalSegments))(
                'Estimated '
              )}
              Total
            </StyledP>
            {showIfOrElse(cartFetching || ratesFetching)(loadingPlaceholder)(
              <CartPrice
                className="afterpay-price-selector-mini-cart"
                isFinalStep={isFinalStep}
                value={getGrandTotalFromTotalSegments(totalSegments)}
              />
            )}
          </CartSummaryItem>
        )}
      </CartSummaryWrapper>
      {showIfOrElse(cartFetching || ratesFetching)(loadingPlaceholder)(<AfterpayMessaging />)}
    </CalloutBlock>
  );
};

const mapStateToProps = (state: GlobalState) => ({
  isVirtualCart: isVirtualCartSelector(state),
  cartLoadingState: cartLoadingStateSelector(state),
  shippingRatesLoadingState: shippingRatesLoadingStateSelector(state),
  shippingRates: shippingRatesSelector(state),
  shippingAddressLoadingState: shippingAddressLoadingStateSelector(state),
  cartItems: cartItemsSelector(state),
  cartItemsLoadingState: cartItemsLoadingStateSelector(state),
  totalSegments: totalSegmentsSelector(state),
  shippingAddress: shippingAddressSelector(state),
  shippingMethod: shippingMethodSelector(state),
  cartPaymentMethods: cartPaymentMethodsSelector(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  removePromoCode: () => dispatch(removePromoCodeRequest()),
  removeGiftCard: (code: string) => dispatch(removeGiftCardRequest(code)),
  removeStoreCredit: () => dispatch(removeStoreCreditRequest()),
  fetchShippingRatesRequest: () => dispatch(_fetchShippingRatesRequest())
});

export default connect(mapStateToProps, mapDispatchToProps)(CartSummary);
